import { TRANSPORT_LOCATİON_ITEM, getAvailableTransportLocations } from "@elit-hair/elitklinik-shared";
import { CalendarMonth } from "@mui/icons-material";
import { Alert, AlertTitle, InputAdornment } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { motion } from "motion/react";
import { useMemo } from "react";
import { Controller, ControllerProps, FieldError, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbArrowMoveRight, TbInfoCircle } from "react-icons/tb";

import { CommonDatePickerPlain, CommonLabel } from "@/components/CommonInput";
import LocationField from "@/components/LocationField";
import { getValueByPath } from "@/helpers/object";
import { PlanFilterStatuses } from "@/stores/use-plan-table";

import { CreatePassengerForm, LocationSchema } from "./CreateCustomPassengerDialog";
import { CreatePassengerDestinationType } from "./CreatePassengerDestinationType";
import CreatePassengerFlightInfo from "./CreatePassengerFlightInfo";
import CreatePassengerWelcomeTeamVisibility from "./CreatePassengerWelcomeTeamVisibility";

export const operationComponents = [
    { value: PlanFilterStatuses.ARRIVAL, formBasePath: "step2.arrival.info" },
    { value: PlanFilterStatuses.DEPARTURE, formBasePath: "step2.departure.info" },
] as const;

const cardVariants = {
    initial: (first: boolean) => ({
        opacity: 0,
        translateX: first ? "100%" : "-100%",
    }),
    animate: {
        opacity: 1,
        translateX: 0,
    },
    exit: (first: boolean) => ({
        opacity: 0,
        translateX: first ? "100%" : "-100%",
    }),
};

interface CreateUserTransprtInfoProps {
    loading: boolean;
    arrivalOrigin: TRANSPORT_LOCATİON_ITEM | null;
    arrivalDestination: TRANSPORT_LOCATİON_ITEM | null;
    departureOrigin: TRANSPORT_LOCATİON_ITEM | null;
    departureDestination: TRANSPORT_LOCATİON_ITEM | null;
    validLocations: { label: string; value: string }[];
    addFlightTimeInfo: boolean;
    activeType: PlanFilterStatuses;
    addArrival: string;
    addDeparture: string;
    showToWelcomeTeam: boolean;
    handleResetLatency: () => void;
}

interface TransportInfoProps {
    loading: boolean;
    destination: TRANSPORT_LOCATİON_ITEM | null;
    origin: TRANSPORT_LOCATİON_ITEM | null;
    validLocations: { label: string; value: string }[];
    addFlightTimeInfo: boolean;
    formBasePath: "step2.arrival.info" | "step2.departure.info";
    checkboxSelected: boolean;
    value: PlanFilterStatuses;
    showToWelcomeTeam: boolean;
    isArrival: boolean;
}

interface TransportSectionProps {
    loading: boolean;
    formBasePath: "step2.arrival.info" | "step2.departure.info";
    origin: TRANSPORT_LOCATİON_ITEM | null;
    destination: TRANSPORT_LOCATİON_ITEM | null;
    validLocations?: { label: string; value: string }[];
    isArrival: boolean;
}

function CreateUserTransprtInfo({
    arrivalOrigin,
    arrivalDestination,
    departureOrigin,
    departureDestination,
    loading,
    validLocations,
    addFlightTimeInfo,
    activeType,
    addArrival,
    addDeparture,
    showToWelcomeTeam,
}: CreateUserTransprtInfoProps) {
    const { origin, destination } = useMemo(
        () =>
            activeType === PlanFilterStatuses.ARRIVAL
                ? {
                      origin: arrivalOrigin,
                      destination: arrivalDestination,
                  }
                : {
                      origin: departureOrigin,
                      destination: departureDestination,
                  },
        [arrivalOrigin, arrivalDestination, departureOrigin, departureDestination, activeType]
    );

    return (
        <>
            <span className="text-sm text-gray-500 col-span-full flex gap-1 pb-4 items-center">
                <TbInfoCircle fontSize={16} />
                Yolcunun transfer bilgilerini doldurunuz.
            </span>
            <div className="grid grid-cols-8 gap-y-5 gap-x-10">
                <CreatePassengerDestinationType activeType={activeType} />
                {operationComponents.map(({ value, formBasePath }, index) => {
                    const isActive = activeType === value;
                    const isArrival = value === PlanFilterStatuses.ARRIVAL;
                    const checkboxSelected = isArrival ? addArrival === "true" : addDeparture === "true";

                    return (
                        isActive && (
                            <motion.div
                                key={index}
                                variants={cardVariants}
                                animate="animate"
                                initial="initial"
                                layoutId="transport-info"
                                custom={index === 0}
                                transition={{ duration: 0.1, type: "tween" }}
                                exit="exit"
                                className="grid grid-cols-8 gap-y-5 gap-x-10 col-span-full"
                            >
                                <TransportInfo
                                    origin={origin}
                                    destination={destination}
                                    showToWelcomeTeam={showToWelcomeTeam}
                                    loading={loading}
                                    validLocations={validLocations}
                                    addFlightTimeInfo={addFlightTimeInfo}
                                    checkboxSelected={checkboxSelected}
                                    value={value}
                                    formBasePath={formBasePath}
                                    isArrival={value === PlanFilterStatuses.ARRIVAL}
                                />
                            </motion.div>
                        )
                    );
                })}
            </div>
        </>
    );
}

function TransportInfo({
    loading,
    formBasePath,
    addFlightTimeInfo,
    checkboxSelected,
    origin,
    destination,
    showToWelcomeTeam,
    isArrival,
}: TransportInfoProps) {
    const { t, i18n } = useTranslation();
    const { control, formState } = useFormContext<CreatePassengerForm>();

    const locationErrorExists = useMemo(
        () =>
            [formBasePath + ".origin", formBasePath + ".destination"].some((path) => {
                const error = getValueByPath<FieldError>(formState.errors, path);
                return error?.message;
            }),
        [formState, formBasePath]
    );

    if (!checkboxSelected) {
        return (
            <Alert severity="info" className="col-span-full">
                <AlertTitle>Transfer bilgisi kaldırıldı</AlertTitle>
                <span className="font-semibold">{isArrival ? "Geliş" : "Gidiş"}</span> bilgisini kapattınız.Yolcu
                transfer bilgisi eklemek için yukarda bulunan seçim butonunu aktifleştirebilirsiniz.
            </Alert>
        );
    }

    const pickUpError = getValueByPath<FieldError>(
        formState.errors,
        `step2.${isArrival ? "arrival" : "departure"}.info.pickUp`
    );
    const helperText = pickUpError?.message || "";

    return (
        <>
            <CommonLabel label={t("pickUpTime")} className="col-span-4">
                <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
                    <Controller
                        name={
                            (formBasePath + ".pickUp") as ControllerProps<
                                CreatePassengerForm,
                                "step2.arrival.info.pickUp"
                            >["name"]
                        }
                        control={control}
                        render={({ field }) => (
                            <CommonDatePickerPlain
                                {...field}
                                ampm={false}
                                disabled={loading}
                                slotProps={{
                                    textField: {
                                        variant: "filled",
                                        error: !!pickUpError,
                                        helperText,
                                        InputProps: {
                                            startAdornment: (
                                                <InputAdornment className="-translate-y-2" position="start">
                                                    <CalendarMonth fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        },
                                    },
                                }}
                                onChange={(value) => field.onChange(value)}
                            />
                        )}
                    />
                </LocalizationProvider>
            </CommonLabel>

            <div className="col-span-full grid grid-cols-[1fr,40px,1fr]">
                <OriginSection
                    loading={loading}
                    formBasePath={formBasePath}
                    origin={origin}
                    destination={destination}
                    isArrival={isArrival}
                />

                <TbArrowMoveRight
                    fontSize={25}
                    className={
                        "!shrink-0 !self-center duration-200 mx-auto " +
                        (locationErrorExists ? "translate-y-0" : "translate-y-3")
                    }
                />

                <DestinationSection
                    loading={loading}
                    formBasePath={formBasePath}
                    origin={origin}
                    destination={destination}
                    isArrival={isArrival}
                />
            </div>
            <CreatePassengerFlightInfo
                formBasePath={formBasePath}
                addFlightTimeInfo={addFlightTimeInfo}
                loading={loading}
            />
            {isArrival && (
                <CreatePassengerWelcomeTeamVisibility
                    showToWelcomeTeam={showToWelcomeTeam}
                    formBasePath={formBasePath}
                />
            )}
        </>
    );
}

function DestinationSection({ loading, formBasePath, destination, origin, isArrival }: TransportSectionProps) {
    const availableLocations = useMemo(() => {
        return getAvailableTransportLocations(isArrival, false, origin?.value);
    }, [isArrival, origin]);

    return (
        <LocationField<CreatePassengerForm>
            className="col-span-1"
            formPath={
                (formBasePath + ".destination") as ControllerProps<
                    CreatePassengerForm,
                    "step2.arrival.info.destination"
                >["name"]
            }
            label="destination"
            location={destination as LocationSchema | null}
            loading={loading}
            validLocations={availableLocations}
        />
    );
}

function OriginSection({
    loading,
    formBasePath,
    origin,
    destination,
    isArrival,
}: Omit<TransportSectionProps, "validLocations">) {
    const availableLocations = useMemo(() => {
        return getAvailableTransportLocations(isArrival, true, destination?.value);
    }, [isArrival, destination]);

    return (
        <LocationField<CreatePassengerForm>
            className="col-span-1"
            formPath={
                (formBasePath + ".origin") as ControllerProps<CreatePassengerForm, "step2.arrival.info.origin">["name"]
            }
            label="origin"
            location={origin as LocationSchema | null}
            loading={loading}
            validLocations={availableLocations}
        />
    );
}

export default CreateUserTransprtInfo;
